"use client";

import React from 'react';
import { Button } from "./button";
import { AlertTriangle, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";

/**
 * Enhanced Confirmation Dialog
 * Supports both legacy props (open, onOpenChange, loading, message) 
 * and new props (isOpen, onClose, isLoading, description)
 */
const ConfirmDialog = ({
  isOpen,
  open,
  onClose,
  onOpenChange,
  onConfirm,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  message,
  confirmText = "Yes",
  cancelText = "Cancel",
  isLoading = false,
  loading = false,
  variant = "destructive"
}) => {

  // Resolve props for backward compatibility
  const actualOpen = isOpen !== undefined ? isOpen : open;
  const actualLoading = isLoading || loading;
  const actualDescription = message || description;

  const getIcon = () => {
    switch (variant) {
      case 'destructive':
        return <AlertTriangle className="h-6 w-6 text-red-500" />;
      case 'success':
        return <CheckCircle2 className="h-6 w-6 text-green-500" />;
      case 'warning':
        return <AlertCircle className="h-6 w-6 text-yellow-500" />;
      default:
        return <AlertTriangle className="h-6 w-6 text-red-500" />;
    }
  };

  const getBgColor = () => {
    switch (variant) {
      case 'destructive':
        return 'bg-red-50 dark:bg-red-900/20';
      case 'success':
        return 'bg-green-50 dark:bg-green-900/20';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/20';
      default:
        return 'bg-red-50 dark:bg-red-900/20';
    }
  };

  const handleClose = () => {
    if (actualLoading) return;
    if (onClose) onClose();
    if (onOpenChange) onOpenChange(false);
  };

  return (
    <Dialog open={actualOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className={`p-1.5 sm:p-2 rounded-full ${getBgColor()}`}>
              {getIcon()}
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                {title}
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <div className="py-2 sm:py-4">
          <DialogDescription className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {actualDescription}
          </DialogDescription>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={actualLoading}
            className="w-full sm:w-auto text-xs sm:text-sm"
          >
            {cancelText}
          </Button>

          <Button
            type="button"
            variant={variant}
            onClick={onConfirm}
            disabled={actualLoading}
            className="w-full sm:w-auto min-w-[80px] sm:min-w-[100px] text-xs sm:text-sm"
          >
            {actualLoading ? 'Processing...' : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
